import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShoppingCartIcon,
    ShoppingBagIcon,
    Bars3Icon,
    XMarkIcon,
    UserIcon,
    ArrowRightOnRectangleIcon,
    Squares2X2Icon,
    ChevronDownIcon
} from '@heroicons/react/24/outline';
import { useAppContext } from '../context/AppContext';
import logo from '../images/FINAL-DESIGN-LOGO.png';

const Navigation = () => {
    const { user, cart, logout } = useAppContext();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const isHomePage = location.pathname === '/';

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

    // Helper to close menu after navigation
    const handleMobileNav = (to, isButton = false) => {
        setIsMenuOpen(false);
        if (isButton) {
            logout();
        } else {
            navigate(to);
        }
    };

    return (
        <nav
            id="nav-main"
            className={`fixed w-full top-0 z-50 transition-all duration-500 bg-white/80 backdrop-blur-lg border-b border-white/20 ${scrolled ? 'py-2' : 'py-4'}`}
        >
            <div id="nav-container" className="w-full px-4 sm:px-6 lg:px-8">
                <div id="nav-flex" className={`flex items-center justify-between transition-all duration-500 ${scrolled ? 'h-16' : 'h-20'}`}>
                    {/* Left: Logo */}
                    <div className="flex-shrink-0 flex items-center">
                        <Link id="nav-logo-link" to="/" className="flex items-center group">
                            <img src={logo} alt="SR Daily Nutrition" className={`${scrolled ? 'h-12' : 'h-16'} w-auto transition-all duration-500`} />
                        </Link>
                    </div>

                    {/* Right: All Navigation Links (hidden on mobile) */}
                    <div className="hidden md:flex items-center space-x-8">
                        <Link id="nav-link-home" to="/" className="text-neutral-700 hover:text-[#E31E24] transition-all duration-300 font-medium relative group font-sans">
                            Home
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E31E24] group-hover:w-full transition-all duration-300"></span>
                        </Link>
                        <button
                            onClick={(e) => {
                                const element = document.getElementById('about-us');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth' });
                                } else {
                                    navigate('/', { state: { scrollToAbout: true } });
                                }
                            }}
                            className="text-neutral-700 hover:text-[#E31E24] transition-all duration-300 font-medium relative group font-sans"
                        >
                            About Us
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E31E24] group-hover:w-full transition-all duration-300"></span>
                        </button>
                        <button
                            onClick={(e) => {
                                if (location.pathname !== '/') {
                                    navigate('/');
                                    setTimeout(() => {
                                        const element = document.getElementById('products-section');
                                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                                    }, 100);
                                } else {
                                    const element = document.getElementById('products-section');
                                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                                }
                            }}
                            className="text-neutral-700 hover:text-[#E31E24] transition-all duration-300 font-medium relative group font-sans"
                        >
                            Our Products
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E31E24] group-hover:w-full transition-all duration-300"></span>
                        </button>
                        <button
                            onClick={(e) => {
                                if (location.pathname !== '/') {
                                    navigate('/');
                                    setTimeout(() => {
                                        const element = document.getElementById('locations');
                                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                                    }, 100);
                                } else {
                                    const element = document.getElementById('locations');
                                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                                }
                            }}
                            className="text-neutral-700 hover:text-[#E31E24] transition-all duration-300 font-medium relative group font-sans"
                        >
                            Visit Our Store
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E31E24] group-hover:w-full transition-all duration-300"></span>
                        </button>
                        <Link id="nav-link-blog" to="/blog" className="text-neutral-700 hover:text-[#E31E24] transition-all duration-300 font-medium relative group font-sans">
                            Blog
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E31E24] group-hover:w-full transition-all duration-300"></span>
                        </Link>
                        {/* <Link id="nav-link-collection" to="/products" className="text-neutral-700 hover:text-[#E31E24] transition-all duration-300 font-medium relative group font-sans">
                            Collection
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E31E24] group-hover:w-full transition-all duration-300"></span>
                        </Link> */}

                        {/* Cart, Sign In, Join Us */}
                        <div className="flex items-center space-x-6">
                            {user ? (
                                <>

                                    {user.role === 'admin' && (
                                        <Link to="/admin" className="text-neutral-700 hover:text-[#E31E24] font-medium transition-colors">
                                            Dashboard
                                        </Link>
                                    )}

                                    <div className="relative">
                                        <button
                                            onClick={() => setIsProfileOpen(!isProfileOpen)}
                                            className="flex items-center gap-2 text-neutral-700 hover:text-[#E31E24] transition-colors focus:outline-none"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center text-yellow-900 font-bold text-sm">
                                                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                            </div>
                                            <span className="font-medium hidden lg:block">{user.name || 'User'}</span>
                                            <ChevronDownIcon className="h-4 w-4" />
                                        </button>

                                        <AnimatePresence>
                                            {isProfileOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 10 }}
                                                    className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50"
                                                >
                                                    <Link
                                                        to="/profile"
                                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#E31E24]"
                                                        onClick={() => setIsProfileOpen(false)}
                                                    >
                                                        Profile
                                                    </Link>
                                                    <button
                                                        onClick={() => {
                                                            logout();
                                                            setIsProfileOpen(false);
                                                        }}
                                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#E31E24]"
                                                    >
                                                        Sign Out
                                                    </button>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </>
                            ) : (
                                <Link to="/login" className="text-neutral-700 hover:text-[#E31E24] font-medium font-sans">Sign In</Link>
                            )}
                        </div>
                    </div>

                    {/* Mobile menu button (visible only on mobile) */}
                    <div id="nav-mobile-menu-btn" className="md:hidden flex items-center">
                        <button
                            id="nav-mobile-toggle"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="p-2 text-neutral-700 hover:text-[#E31E24] transition-all duration-300"
                        >
                            {isMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu (only visible when burger is open) */}
                <AnimatePresence>
                    {isMenuOpen && (
                        <motion.div
                            id="nav-mobile-menu"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="md:hidden border-t border-neutral-200 overflow-hidden bg-white"
                        >
                            <div id="nav-mobile-links" className="px-2 pt-4 pb-4 space-y-3">
                                <button onClick={() => handleMobileNav('/')} className="block w-full text-left px-4 py-3 text-neutral-700 hover:text-[#E31E24] hover:bg-orange-50 rounded-xl transition-all font-medium font-sans">Home</button>
                                <button
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        setTimeout(() => {
                                            const element = document.getElementById('about-us');
                                            if (element) {
                                                element.scrollIntoView({ behavior: 'smooth' });
                                            } else {
                                                navigate('/', { state: { scrollToAbout: true } });
                                            }
                                        }, 100);
                                    }}
                                    className="block w-full text-left px-4 py-3 text-neutral-700 hover:text-[#E31E24] hover:bg-orange-50 rounded-xl transition-all font-medium font-sans"
                                >
                                    About Us
                                </button>
                                <button
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        if (location.pathname !== '/') {
                                            navigate('/');
                                            setTimeout(() => {
                                                const element = document.getElementById('products-section');
                                                if (element) element.scrollIntoView({ behavior: 'smooth' });
                                            }, 200);
                                        } else {
                                            setTimeout(() => {
                                                const element = document.getElementById('products-section');
                                                if (element) element.scrollIntoView({ behavior: 'smooth' });
                                            }, 100);
                                        }
                                    }}
                                    className="block w-full text-left px-4 py-3 text-neutral-700 hover:text-[#E31E24] hover:bg-orange-50 rounded-xl transition-all font-medium font-sans"
                                >
                                    Our Products
                                </button>
                                <button
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        if (location.pathname !== '/') {
                                            navigate('/');
                                            setTimeout(() => {
                                                const element = document.getElementById('locations');
                                                if (element) element.scrollIntoView({ behavior: 'smooth' });
                                            }, 200);
                                        } else {
                                            setTimeout(() => {
                                                const element = document.getElementById('locations');
                                                if (element) element.scrollIntoView({ behavior: 'smooth' });
                                            }, 100);
                                        }
                                    }}
                                    className="block w-full text-left px-4 py-3 text-neutral-700 hover:text-[#E31E24] hover:bg-orange-50 rounded-xl transition-all font-medium font-sans"
                                >
                                    Visit Our Store
                                </button>
                                <button onClick={() => handleMobileNav('/blog')} className="block w-full text-left px-4 py-3 text-neutral-700 hover:text-[#E31E24] hover:bg-orange-50 rounded-xl transition-all font-medium font-sans">Blog</button>
                                {/* <button onClick={() => handleMobileNav('/products')} className="block w-full text-left px-4 py-3 text-neutral-700 hover:text-[#E31E24] hover:bg-orange-50 rounded-xl transition-all font-medium font-sans">Collection</button> */}
                                {user ? (
                                    <>
                                        {user.role === 'admin' && (
                                            <button onClick={() => handleMobileNav('/admin')} className="block w-full text-left px-4 py-3 text-[#E31E24] hover:bg-orange-50 rounded-xl transition-all font-semibold font-sans">
                                                Admin Dashboard
                                            </button>
                                        )}
                                        <button onClick={() => handleMobileNav('/profile')} className="block w-full text-left px-4 py-3 text-neutral-700 hover:text-[#E31E24] hover:bg-orange-50 rounded-xl transition-all font-medium font-sans">Profile</button>
                                        <button
                                            onClick={() => handleMobileNav('', true)}
                                            className="block w-full text-left px-4 py-3 text-neutral-700 hover:text-[#E31E24] hover:bg-orange-50 rounded-xl transition-all font-medium font-sans"
                                        >
                                            Sign Out
                                        </button>
                                    </>
                                ) : (
                                    <button onClick={() => handleMobileNav('/login')} className="block w-full text-left px-4 py-3 text-neutral-700 hover:text-[#E31E24] hover:bg-orange-50 rounded-xl transition-all font-medium font-sans">Sign In</button>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </nav>
    );
};

export default Navigation;
